import React, { useCallback } from "react";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useLocale from "@RHCommerceDev/hooks/useLocale";
import { useAppId } from "@RHCommerceDev/hooks/useAppId";
import parsedCatalogNavigation from "@RHCommerceDev/utils/parseCatalogNavigationData";
import memoize from "@RHCommerceDev/utils/memoize";
import CatalogNavigation from "@RHCommerceDev/container-rhr-header/CatalogNavigation/CatalogNavigation";
import buildPath from "@RHCommerceDev/utils/buildPath";
import { useHistory } from "react-router";

const CatalogNavigationContainer = () => {
  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const locale = useLocalization();
  const splitLocale = useLocale();
  const { isConcierge } = useAppId();
  const history = useHistory();

  const {
    state: headerState,
    closeCategoryDropdown,
    openCategoryDropdown
  } = useHeaderAtom();

  // Parse navigation into structured categories
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    isConcierge
  );
  const categories = navigationData?.childCategories ?? [];

  const isFrenchMontreal = splitLocale === "fr-CA" || splitLocale === "fr-FR";

  //onClick function is category is link
  const handleCategoryLinkClick = useCallback(
    (event: React.MouseEvent, category: NavigationElement) => {
      // Dispatch analytics event
      const target = event?.target as HTMLElement;
      const { id, className } = target;
      document?.body?.dispatchEvent(
        new CustomEvent("cta_click", {
          detail: {
            item: {
              isHeaderNavigationNew: true,
              topCat: category?.displayName,
              parentCat: category?.targetUrl,
              cat: category?.targetUrl,
              catURL: category?.targetUrl,
              class: className,
              id: id
            }
          }
        })
      );

      if (
        category?.linkType === "none" ||
        category?.targetUrl?.startsWith("https://")
      ) {
        if (typeof window !== "undefined") {
          window.open(category?.targetUrl ?? "", "_blank");
        }
      } else {
        history.push(buildPath(`${locale}${category?.targetUrl}`));
      }
    },
    [history, locale]
  );

  //onClick function if category is dropdown
  const handleCategoryDropdownToggle = useCallback(
    (categoryIndex: number) => {
      if (typeof window !== undefined) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      if (categoryIndex === headerState.selectedCategoryIndex) {
        closeCategoryDropdown();
      } else {
        openCategoryDropdown(categoryIndex);
      }
    },
    [
      headerState.selectedCategoryIndex,
      closeCategoryDropdown,
      openCategoryDropdown
    ]
  );

  return (
    <CatalogNavigation
      categories={categories}
      selectedCategoryIndex={headerState.selectedCategoryIndex}
      isFrenchMontreal={isFrenchMontreal}
      isConcierge={isConcierge}
      onCategoryLinkClick={handleCategoryLinkClick}
      onCategoryDropdownToggle={handleCategoryDropdownToggle}
    />
  );
};

export default memoize(CatalogNavigationContainer);
